import {MAX_OPEN_CONVERSATION_TABS} from './routeAgentWireEvent'

/**
 * The chat's open tabs, remembered across a page refresh. Only conversations the server knows are
 * stored — an untouched draft has nothing to reload, so restoring it would just fail.
 */
export interface OpenConversationTabs {
  /** Open tabs in tab-strip order. */
  ids: string[]
  /** The focused tab; always one of `ids` when set. */
  activeId: string | null
}

export const AI_CHAT_OPEN_TABS_KEY = 'ai-chat-open-tabs'
/** The single-tab key this replaced; read once so an existing session keeps its chat. */
export const LEGACY_LAST_CONVERSATION_KEY = 'ai-chat-last-conversation'

const EMPTY: OpenConversationTabs = {ids: [], activeId: null}

/** Validate an untrusted stored value: string ids only, de-duplicated, capped at the tab limit. */
export function normalizeOpenConversationTabs(value: unknown): OpenConversationTabs {
  if (!value || typeof value !== 'object') return EMPTY
  const {ids, activeId} = value as {ids?: unknown; activeId?: unknown}
  if (!Array.isArray(ids)) return EMPTY
  const unique = [...new Set(ids.filter((id): id is string => typeof id === 'string' && id !== ''))]
  const capped = unique.slice(-MAX_OPEN_CONVERSATION_TABS)
  const active = typeof activeId === 'string' && capped.includes(activeId) ? activeId : null
  return {ids: capped, activeId: active ?? capped[capped.length - 1] ?? null}
}

export function readOpenConversationTabs(): OpenConversationTabs {
  try {
    const raw = localStorage.getItem(AI_CHAT_OPEN_TABS_KEY)
    if (raw) return normalizeOpenConversationTabs(JSON.parse(raw))
    const legacyId = localStorage.getItem(LEGACY_LAST_CONVERSATION_KEY)
    return legacyId ? {ids: [legacyId], activeId: legacyId} : EMPTY
  } catch {
    return EMPTY /* corrupt JSON or storage blocked */
  }
}

export function writeOpenConversationTabs(tabs: OpenConversationTabs): void {
  try {
    localStorage.setItem(AI_CHAT_OPEN_TABS_KEY, JSON.stringify(normalizeOpenConversationTabs(tabs)))
    localStorage.removeItem(LEGACY_LAST_CONVERSATION_KEY)
  } catch {
    /* ignore quota/private-mode */
  }
}
